// src/pages/QuickOrderForm.js
// Public page — no auth required
// Route: /order/:slug  (slug = seller's facebook_username)
// ?code=X        → single product pre-loaded
// ?codes=X,Y,Z  → multiple products pre-loaded

import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../supabase'
import BDAddressFields from '../components/BDAddressFields'
import PhoneInput from '../components/PhoneInput'

// ── Brand tokens ──────────────────────────────────────────────────────────────
const NAVY    = '#0B1F3A'
const TEAL    = '#2DD4B0'
const WHITE   = '#FFFFFF'
const MUTED   = '#6B7A94'
const BORDER  = '#E2E8F0'
const BG      = '#F7F9FC'
const TEXT    = '#1A2B47'
const ERROR   = '#DC2626'
const SUCCESS = '#059669'
const WARNING = '#D97706'
const FONT_UI   = "'Space Grotesk', sans-serif"
const FONT_BODY = "'Inter', sans-serif"
const FONT_MONO = "'JetBrains Mono', monospace"

const baseInp = {
  width: '100%', border: `1px solid ${BORDER}`, borderRadius: '10px',
  padding: '10px 13px', fontSize: '13px', outline: 'none',
  background: WHITE, color: TEXT, fontFamily: FONT_BODY,
  boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s',
}

function Inp({ value, onChange, placeholder, type = 'text', error, disabled, style: extra, autoFocus }) {
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} disabled={disabled} autoFocus={autoFocus}
      style={{ ...baseInp, borderColor: error ? ERROR : BORDER, opacity: disabled ? 0.6 : 1, ...extra }}
      onFocus={e => { if (!disabled) { e.target.style.borderColor = TEAL; e.target.style.boxShadow = '0 0 0 3px rgba(45,212,176,0.12)' } }}
      onBlur={e => { e.target.style.borderColor = error ? ERROR : BORDER; e.target.style.boxShadow = 'none' }} />
  )
}

function Sel({ value, onChange, children, disabled }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      style={{ ...baseInp, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
      onFocus={e => e.target.style.borderColor = TEAL}
      onBlur={e => e.target.style.borderColor = BORDER}>
      {children}
    </select>
  )
}

function Field({ label, optional, marketing, error, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
        <label style={{ fontSize: '12px', fontWeight: 600, color: error ? ERROR : MUTED, fontFamily: FONT_UI, letterSpacing: '0.02em' }}>
          {label}
          {!optional && <span style={{ color: TEAL, marginLeft: '2px' }}>*</span>}
          {optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>optional</span>}
        </label>
        {marketing && (
          <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '20px', background: 'rgba(45,212,176,0.12)', color: TEAL, border: `1px solid rgba(45,212,176,0.2)` }}>
            for offers
          </span>
        )}
      </div>
      {children}
      {error && <div style={{ fontSize: '11px', color: ERROR, marginTop: '4px' }}>{error}</div>}
    </div>
  )
}

function SectionCard({ title, subtitle, icon, children, style: extra }) {
  return (
    <div style={{ background: WHITE, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: '0 2px 12px rgba(11,31,58,0.06)', marginBottom: '16px', overflow: 'hidden', ...extra }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${BORDER}`, background: BG, display: 'flex', alignItems: 'center', gap: '10px' }}>
        {icon && <span style={{ fontSize: '16px' }}>{icon}</span>}
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, fontFamily: FONT_UI }}>{title}</div>
          {subtitle && <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ padding: '18px' }}>{children}</div>
    </div>
  )
}

function StepBar({ current, total, labels }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '14px 20px', background: WHITE, borderBottom: `1px solid ${BORDER}`, gap: '4px', overflowX: 'auto' }}>
      {labels.map((label, i) => {
        const n = i + 1
        const done = n < current
        const active = n === current
        return (
          <div key={n} style={{ display: 'flex', alignItems: 'center', flex: i < labels.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, background: done ? SUCCESS : active ? NAVY : BORDER, color: done || active ? WHITE : MUTED }}>
                {done ? '✓' : n}
              </div>
              <span style={{ fontSize: '11px', fontWeight: active ? 700 : 500, color: active ? TEXT : done ? SUCCESS : MUTED, fontFamily: FONT_UI, whiteSpace: 'nowrap' }}>
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div style={{ flex: 1, height: '2px', background: done ? SUCCESS : BORDER, margin: '0 6px', borderRadius: '2px', minWidth: '12px' }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

function TaraStarIcon({ size = 16, color = TEAL }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M12 2C12 2 12.8 7.5 14.5 9.5C16.2 11.5 22 12 22 12C22 12 16.2 12.5 14.5 14.5C12.8 16.5 12 22 12 22C12 22 11.2 16.5 9.5 14.5C7.8 12.5 2 12 2 12C2 12 7.8 11.5 9.5 9.5C11.2 7.5 12 2 12 2Z" fill={color}/>
      <path d="M19 3C19 3 19.4 5.2 20.2 6C21 6.8 23 7 23 7C23 7 21 7.2 20.2 8C19.4 8.8 19 11 19 11C19 11 18.6 8.8 17.8 8C17 7.2 15 7 15 7C15 7 17 6.8 17.8 6C18.6 5.2 19 3 19 3Z" fill={color} opacity="0.7"/>
      <path d="M5 16C5 16 5.25 17.3 5.75 17.75C6.25 18.25 7.5 18.5 7.5 18.5C7.5 18.5 6.25 18.75 5.75 19.25C5.25 19.75 5 21 5 21C5 21 4.75 19.75 4.25 19.25C3.75 18.75 2.5 18.5 2.5 18.5C2.5 18.5 3.75 18.25 4.25 17.75C4.75 17.3 5 16 5 16Z" fill={color} opacity="0.5"/>
    </svg>
  )
}

function Btn({ onClick, disabled, loading, children, variant = 'primary', fullWidth, size = 'md' }) {
  const pad = size === 'lg' ? '14px 20px' : size === 'sm' ? '8px 14px' : '11px 18px'
  const fs  = size === 'lg' ? '15px' : size === 'sm' ? '12px' : '13px'
  const styles = {
    primary: { background: disabled || loading ? MUTED : `linear-gradient(135deg,${NAVY},#1a3a6e)`, color: WHITE, border: 'none' },
    outline: { background: WHITE, color: TEXT, border: `1px solid ${BORDER}` },
    teal:    { background: `linear-gradient(135deg,${TEAL},#22c9a8)`, color: NAVY, border: 'none' },
    ghost:   { background: 'transparent', color: MUTED, border: `1px solid ${BORDER}` },
  }
  return (
    <button onClick={onClick} disabled={disabled || loading}
      style={{ padding: pad, borderRadius: '10px', fontSize: fs, fontWeight: 700, cursor: disabled || loading ? 'not-allowed' : 'pointer', fontFamily: FONT_UI, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', width: fullWidth ? '100%' : 'auto', transition: 'all 0.15s', ...styles[variant] }}
      onMouseEnter={e => { if (!disabled && !loading) e.currentTarget.style.transform = 'translateY(-1px)' }}
      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
      {loading && <div style={{ width: '14px', height: '14px', border: `2px solid rgba(255,255,255,0.3)`, borderTopColor: variant === 'teal' ? NAVY : TEAL, borderRadius: '50%', animation: 'qof-spin 0.7s linear infinite' }} />}
      {children}
    </button>
  )
}

function CourierPreview({ address, name, phone, onEdit }) {
  const parts = [
    address.house && address.house !== 'N/A' ? address.house : null,
    address.road && address.road !== 'N/A' ? address.road : null,
    address.moholla && address.moholla !== 'N/A' ? address.moholla : null,
    address.neighbourhood && address.neighbourhood !== '__na__' ? address.neighbourhood : null,
    address.metro_thana && address.metro_thana !== '__na__' ? address.metro_thana : null,
    address.union_name && address.union_name !== '__na__' ? address.union_name : null,
    address.thana && address.thana !== '__na__' ? address.thana : null,
    address.district && address.district !== 'N/A' ? address.district : null,
    address.division && address.division !== 'N/A' ? address.division : null,
  ].filter(Boolean)

  return (
    <div style={{ borderRadius: '12px', border: `1.5px solid ${TEAL}`, background: 'rgba(45,212,176,0.05)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: 'rgba(45,212,176,0.1)', borderBottom: `1px solid rgba(45,212,176,0.2)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '13px' }}>📦</span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: NAVY, fontFamily: FONT_UI }}>Delivery Label Preview</span>
        </div>
        <button onClick={onEdit} style={{ fontSize: '11.5px', fontWeight: 600, color: TEAL, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px', borderRadius: '6px' }}>
          Edit address
        </button>
      </div>
      <div style={{ padding: '14px 16px', fontFamily: FONT_MONO, fontSize: '12.5px', color: TEXT, lineHeight: 2 }}>
        <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>{name}</div>
        <div style={{ color: MUTED }}>{phone}</div>
        <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: `1px dashed ${BORDER}` }}>
          {parts.map((p, i) => <div key={i}>{p}</div>)}
          {address.zip && <div>ZIP: {address.zip}</div>}
        </div>
      </div>
      <div style={{ padding: '10px 14px', background: BG, borderTop: `1px solid ${BORDER}` }}>
        <div style={{ fontSize: '11.5px', color: MUTED }}>✓ Please confirm this is your correct delivery address before proceeding</div>
      </div>
    </div>
  )
}

// ── Anti-spam ─────────────────────────────────────────────────────────────────
async function checkSpam(sellerId, phone) {
  const tenMinsAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString()
  const { count } = await supabase.from('seller_order_form_submissions')
    .select('*', { count: 'exact', head: true })
    .eq('seller_id', sellerId).eq('customer_phone', phone).gte('created_at', tenMinsAgo)
  return (count || 0) > 0
}

function currSym(currency) {
  return { AUD: 'A$', USD: '$', GBP: '£', EUR: '€' }[currency] || '৳'
}

// ── CartItem — single product row in cart ──────────────────────────────────────
function CartItem({ item, index, onUpdate, onRemove, sym, canRemove }) {
  const [codeInput, setCodeInput]         = useState(item.searchQuery || '')
  const [searching, setSearching]         = useState(false)
  const [results, setResults]             = useState([])
  const [showDrop, setShowDrop]           = useState(false)
  const [notFound, setNotFound]           = useState(false)
  const [loadingVariants, setLoadingVariants] = useState(false)
  const dropRef      = useRef(null)
  const searchTimer  = useRef(null)

  useEffect(() => {
    const h = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  useEffect(() => {
    if (!item.product_id) return
    setLoadingVariants(true)
    supabase.from('product_variants')
      .select('id,variant_name,variant_code,stock,sell_price')
      .eq('product_id', item.product_id).order('variant_name')
      .then(({ data }) => {
        const vList = data || []
        onUpdate(index, { variants: vList })
        if (vList.length === 1) onUpdate(index, { variants: vList, variant_id: vList[0].id, variant_name: vList[0].variant_name, unit_price: vList[0].sell_price || item.unit_price })
        setLoadingVariants(false)
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.product_id])

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); setShowDrop(false); return }
    setSearching(true); setNotFound(false)
    const { data } = await supabase.from('products')
      .select('id,name,product_code,sell_price,stock,color,product_type,image_url,variable_pricing,best_price,advance_payment_type,advance_payment_value,seller_id')
      .eq('seller_id', item.seller_id).eq('status', 'active')
      .or(`product_code.ilike.%${q}%,name.ilike.%${q}%,color.ilike.%${q}%`)
      .limit(8)
    setResults(data || []); setNotFound((data || []).length === 0); setShowDrop(true); setSearching(false)
  }, [item.seller_id])

  const handleCodeChange = (v) => {
    setCodeInput(v); clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => doSearch(v), 350)
  }

  const pickProduct = (p) => {
    onUpdate(index, {
      product_id: p.id, product_code: p.product_code, product_name: p.name,
      product_color: p.color, product_image: p.image_url,
      unit_price: p.sell_price || 0, variable_pricing: p.variable_pricing,
      best_price: p.best_price,
      advance_payment_type: p.advance_payment_type || 'none',
      advance_payment_value: p.advance_payment_value || null,
      variants: [], variant_id: '', variant_name: '',
    })
    setCodeInput(p.product_code); setShowDrop(false); setNotFound(false)
  }

  const clearProduct = () => {
    onUpdate(index, { product_id: '', product_code: '', product_name: '', product_color: '', product_image: '', unit_price: 0, advance_payment_type: 'none', advance_payment_value: null, variants: [], variant_id: '', variant_name: '' })
    setCodeInput(''); setResults([])
  }

  const selectedVariant = (item.variants || []).find(v => v.id === item.variant_id)
  const linePrice = (selectedVariant?.sell_price || item.unit_price || 0) * (item.quantity || 1)

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: '14px', padding: '16px', marginBottom: '12px', background: WHITE }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: FONT_UI }}>Item {index + 1}</div>
        {canRemove && (
          <button onClick={() => onRemove(index)}
            style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#FEF2F2', border: '1px solid #FECACA', color: ERROR, fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
            ×
          </button>
        )}
      </div>

      {!item.product_id ? (
        <div ref={dropRef} style={{ position: 'relative', marginBottom: '12px' }}>
          <div style={{ position: 'relative' }}>
            <input value={codeInput} onChange={e => handleCodeChange(e.target.value)}
              placeholder="Type product code or name..."
              style={{ ...baseInp, paddingLeft: '36px' }}
              onFocus={() => codeInput.length >= 2 && setShowDrop(true)}/>
            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>
              {searching
                ? <div style={{ width: '13px', height: '13px', border: `2px solid ${BORDER}`, borderTopColor: TEAL, borderRadius: '50%', animation: 'qof-spin 0.7s linear infinite' }} />
                : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              }
            </div>
          </div>
          {showDrop && results.length > 0 && (
            <div style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', zIndex: 200, border: `1px solid ${BORDER}`, borderRadius: '12px', background: WHITE, boxShadow: '0 8px 24px rgba(11,31,58,0.12)', maxHeight: '260px', overflowY: 'auto' }}>
              {results.map(p => (
                <div key={p.id} onMouseDown={() => pickProduct(p)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 14px', borderBottom: `1px solid ${BORDER}`, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = BG}
                  onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                  {p.image_url
                    ? <img src={p.image_url} alt={p.name} style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: BG, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>📦</div>
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '2px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{p.name}</span>
                      {p.color && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '20px', background: '#F5F3FF', color: '#6D28D9', fontWeight: 600 }}>{p.color}</span>}
                      {p.stock === 0 && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '20px', background: '#FEF2F2', color: ERROR, fontWeight: 600 }}>Out of stock</span>}
                    </div>
                    <div style={{ fontSize: '11px', color: NAVY, fontFamily: FONT_MONO, fontWeight: 600 }}>{p.product_code}</div>
                    {p.advance_payment_type && p.advance_payment_type !== 'none' && (
                      <div style={{ fontSize: '10.5px', color: '#0D9488', marginTop: '2px' }}>
                        💳 {p.advance_payment_type === 'full' ? 'Full advance required' : p.advance_payment_type === 'percent' ? p.advance_payment_value + '% advance' : sym + p.advance_payment_value + ' advance'}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT, flexShrink: 0 }}>{sym}{Number(p.sell_price || 0).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
          {notFound && (
            <div style={{ marginTop: '8px', padding: '10px 13px', borderRadius: '10px', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '12.5px', color: ERROR }}>
              ⚠ No product found for "{codeInput}" — please check the code from your chat or contact the shop.
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: '12px 14px', borderRadius: '12px', background: 'rgba(45,212,176,0.07)', border: `1.5px solid rgba(45,212,176,0.25)`, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          {item.product_image
            ? <img src={item.product_image} alt={item.product_name} style={{ width: '48px', height: '48px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
            : <div style={{ width: '48px', height: '48px', borderRadius: '10px', background: BG, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>📦</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '2px' }}>
              <span style={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>{item.product_name}</span>
              {item.product_color && <span style={{ fontSize: '10.5px', padding: '1px 7px', borderRadius: '20px', background: '#F5F3FF', color: '#6D28D9', fontWeight: 600 }}>{item.product_color}</span>}
            </div>
            <div style={{ fontSize: '11px', color: NAVY, fontFamily: FONT_MONO, fontWeight: 600 }}>{item.product_code}</div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: TEAL, marginTop: '3px' }}>
              {sym}{Number(selectedVariant?.sell_price || item.unit_price || 0).toLocaleString()}
            </div>
            {/* Advance payment badge per item */}
            {item.advance_payment_type && item.advance_payment_type !== 'none' && (
              <div style={{ marginTop: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '20px', background: 'rgba(13,148,136,0.1)', border: '1px solid rgba(13,148,136,0.2)' }}>
                <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#0D9488' }}>
                  {item.advance_payment_type === 'full'
                    ? '💳 Full payment required'
                    : item.advance_payment_type === 'percent'
                      ? `💳 ${item.advance_payment_value}% advance required`
                      : `💳 ${sym}${item.advance_payment_value} advance required`}
                </span>
              </div>
            )}
          </div>
          <button onClick={clearProduct}
            style={{ fontSize: '12px', fontWeight: 600, padding: '6px 12px', borderRadius: '8px', border: `1px solid ${BORDER}`, background: WHITE, color: MUTED, cursor: 'pointer', flexShrink: 0 }}>
            Change
          </button>
        </div>
      )}

      {/* Variant picker */}
      {item.product_id && (
        loadingVariants ? (
          <div style={{ fontSize: '12px', color: MUTED, padding: '6px 0', marginBottom: '12px' }}>Loading options...</div>
        ) : (item.variants || []).length === 0 ? (
          <div style={{ fontSize: '12px', color: SUCCESS, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            ✓ No size or option needed for this product
          </div>
        ) : (
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, fontFamily: FONT_UI, marginBottom: '8px' }}>
              Choose your option <span style={{ color: ERROR }}>*</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {(item.variants || []).map(v => {
                const isSelected = item.variant_id === v.id
                const isOut = v.stock === 0
                const isLow = v.stock > 0 && v.stock <= 5
                return (
                  <button key={v.id}
                    onClick={() => !isOut && onUpdate(index, { variant_id: v.id, variant_name: v.variant_name, unit_price: v.sell_price || item.unit_price })}
                    disabled={isOut}
                    style={{ padding: '9px 14px', borderRadius: '10px', cursor: isOut ? 'not-allowed' : 'pointer', border: `2px solid ${isSelected ? TEAL : isLow ? '#FDE68A' : BORDER}`, background: isSelected ? 'rgba(45,212,176,0.1)' : isOut ? BG : WHITE, opacity: isOut ? 0.5 : 1, textAlign: 'left', transition: 'all 0.12s' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: isSelected ? TEAL : isOut ? MUTED : TEXT }}>
                      {v.variant_name}{isSelected ? ' ✓' : ''}
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '2px', color: isOut ? ERROR : isLow ? WARNING : MUTED }}>
                      {isOut ? 'Unavailable' : isLow ? `Only ${v.stock} left` : 'Available'}
                      {v.sell_price ? ` · ${sym}${Number(v.sell_price).toLocaleString()}` : ''}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )
      )}

      {/* Qty + line total */}
      {item.product_id && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: MUTED, fontFamily: FONT_UI }}>Qty</span>
            <button onClick={() => onUpdate(index, { quantity: Math.max(1, (item.quantity || 1) - 1) })}
              style={{ width: '32px', height: '32px', borderRadius: '8px', border: `1px solid ${BORDER}`, background: BG, fontSize: '16px', fontWeight: 700, color: TEXT, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
            <input type="number" value={item.quantity || 1} min="1"
              onChange={e => onUpdate(index, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
              style={{ ...baseInp, width: '60px', textAlign: 'center', fontWeight: 700 }} />
            <button onClick={() => onUpdate(index, { quantity: (item.quantity || 1) + 1 })}
              style={{ width: '32px', height: '32px', borderRadius: '8px', border: `1px solid ${BORDER}`, background: BG, fontSize: '16px', fontWeight: 700, color: TEXT, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
          </div>
          {linePrice > 0 && (
            <div style={{ fontSize: '15px', fontWeight: 700, color: TEXT, fontFamily: FONT_UI }}>
              {sym}{linePrice.toLocaleString()}
            </div>
          )}
        </div>
      )}

      {item.product_id && (item.variants || []).length > 0 && !item.variant_id && (
        <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '8px', background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: '12px', color: WARNING, fontWeight: 500 }}>
          ⚠ Please choose an option above before continuing
        </div>
      )}
    </div>
  )
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function QuickOrderForm() {
  const { slug }       = useParams()
  const [searchParams] = useSearchParams()

  const preCode  = searchParams.get('code')  || ''
  const preCodes = searchParams.get('codes') || ''
  const allCodes = preCodes ? preCodes.split(',').map(c => c.trim()).filter(Boolean) : preCode ? [preCode] : []

  const [step, setStep]                     = useState(1)
  const [seller, setSeller]                 = useState(null)
  const [taraSettings, setTaraSettings]     = useState(null)
  const [loading, setLoading]               = useState(true)
  const [notFound, setNotFound]             = useState(false)
  const [formDisabled, setFormDisabled]     = useState(false)

  // Step 1 — Phone
  const [countryCode, setCountryCode]             = useState('+880')
  const [phone, setPhone]                         = useState('')
  const [lookingUp, setLookingUp]                 = useState(false)
  const [existingCustomer, setExistingCustomer]   = useState(null)
  const [lookupDone, setLookupDone]               = useState(false)
  const [phoneError, setPhoneError]               = useState('')

  // Step 2 — Details + Address
  const [name, setName] = useState('')
  const [address, setAddress] = useState({
    house: '', road: '', moholla: '', thana: '', thana_id: '',
    district: '', district_id: '', division: '', division_id: '',
    union_name: '', union_id: '', zip: '', country: 'Bangladesh',
    metro_thana: '', metro_thana_id: '', neighbourhood: '', neighbourhood_id: '',
  })
  const setA = (key, val) => setAddress(a => ({ ...a, [key]: val }))
  const [addressConfirmed, setAddressConfirmed] = useState(false)
  const [detailErrors, setDetailErrors]         = useState({})

  // Step 3 — Cart
  const emptyItem = (sellerId) => ({
    seller_id: sellerId, product_id: '', product_code: '', product_name: '',
    product_color: '', product_image: '', unit_price: 0,
    advance_payment_type: 'none', advance_payment_value: null,
    variants: [], variant_id: '', variant_name: '', quantity: 1, searchQuery: '',
  })
  const [cart, setCart]               = useState([])
  const [cartErrors, setCartErrors]   = useState([])
  const [note, setNote]               = useState('')

  // Step 4 — Profile (optional)
  const [profile, setProfile] = useState({ birthday: '', anniversary: '', gender: '', age_group: '', whatsapp: '', email: '', facebook_url: '', instagram_url: '' })
  const setP = (k, v) => setProfile(p => ({ ...p, [k]: v }))

  // Step 5 — Submit
  const [submitting, setSubmitting]   = useState(false)
  const [submitted, setSubmitted]     = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [spamBlocked, setSpamBlocked] = useState(false)

  const sym   = currSym(seller?.seller_currency)
  const STEPS = ['Phone', 'Your Details', 'Your Order', 'About You', 'Review']

  // ── Load seller ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.from('sellers')
        .select('id,shop_name,logo_url,cover_photo_url,tagline,order_form_enabled,order_form_note,facebook_username,country,seller_currency,seller_mode')
        .eq('facebook_username', slug).single()

      if (error || !data) { setNotFound(true); setLoading(false); return }
      setSeller(data)
      if (!data.order_form_enabled) { setFormDisabled(true); setLoading(false); return }

      const { data: ts } = await supabase.from('tara_settings')
        .select('delivery_dhaka,delivery_outside,delivery_express_note')
        .eq('seller_id', data.id).single()
      setTaraSettings(ts)

      // Set country code based on seller country — default to BD (+880)
      if (data.country) {
        const c = data.country.toLowerCase()
        if (c.includes('australia') || c === 'au') setCountryCode('+61')
        else if (c.includes('united states') || c === 'us' || c === 'usa') setCountryCode('+1')
        else setCountryCode('+880') // default BD for all others
      }

      // Initialize cart from URL codes
      if (allCodes.length > 0) {
        const items = []
        for (const code of allCodes) {
          const { data: prod } = await supabase.from('products')
            .select('id,name,product_code,sell_price,stock,color,image_url,variable_pricing,best_price,advance_payment_type,advance_payment_value')
            .eq('seller_id', data.id).eq('product_code', code).eq('status', 'active').single()
          if (prod) {
            items.push({
              ...emptyItem(data.id),
              product_id: prod.id, product_code: prod.product_code, product_name: prod.name,
              product_color: prod.color || '', product_image: prod.image_url || '',
              unit_price: prod.sell_price || 0, variable_pricing: prod.variable_pricing,
              best_price: prod.best_price,
              advance_payment_type: prod.advance_payment_type || 'none',
              advance_payment_value: prod.advance_payment_value || null,
              searchQuery: prod.product_code,
            })
          }
        }
        setCart(items.length > 0 ? items : [emptyItem(data.id)])
      } else {
        setCart([emptyItem(data.id)])
      }
      setLoading(false)
    }
    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  // ── Phone lookup ─────────────────────────────────────────────────────────────
  const handleLookup = async () => {
    if (!phone || phone.length < 5) { setPhoneError('Please enter a valid phone number'); return }
    setPhoneError(''); setLookingUp(true)
    const fullPhone = countryCode + phone.replace(/^0+/, '')
    const { data: customers } = await supabase.from('customers')
      .select('*').eq('seller_id', seller.id)
      .or(`phone.eq.${fullPhone},phone.eq.${phone}`)
      .order('created_at', { ascending: false }).limit(1)
    const found = customers?.[0] || null
    setExistingCustomer(found); setLookupDone(true)
    if (found) {
      setName(found.name || '')
      setAddress({
        house: found.house || '', road: found.road || '', moholla: found.moholla || '',
        thana: found.thana || '', thana_id: found.thana_id || '',
        district: found.district || '', district_id: found.district_id || '',
        division: found.division || '', division_id: found.division_id || '',
        union_name: found.union_name || '', union_id: found.union_id || '',
        zip: found.zip || '', country: found.country || 'Bangladesh',
        metro_thana: found.metro_thana || '', metro_thana_id: found.metro_thana_id || '',
        neighbourhood: found.neighbourhood || '', neighbourhood_id: found.neighbourhood_id || '',
      })
      setProfile({ birthday: found.birthday || '', anniversary: found.anniversary || '', gender: found.gender || '', age_group: found.age_range || '', whatsapp: found.whatsapp || '', email: found.email || '', facebook_url: found.facebook_url || '', instagram_url: found.instagram_url || '' })
    }
    setLookingUp(false)
  }

  // ── Cart helpers ─────────────────────────────────────────────────────────────
  const updateItem = (index, changes) => setCart(c => c.map((item, i) => i === index ? { ...item, ...changes } : item))
  const removeItem = (index) => setCart(c => c.filter((_, i) => i !== index))
  const addItem    = () => setCart(c => [...c, emptyItem(seller.id)])

  const cartTotal = cart.reduce((sum, item) => {
    const v = (item.variants || []).find(v => v.id === item.variant_id)
    return sum + (v?.sell_price || item.unit_price || 0) * (item.quantity || 1)
  }, 0)

  // Per-item advance breakdown (used in review step)
  const advanceItems = cart.filter(it => it.advance_payment_type && it.advance_payment_type !== 'none')
  const getAdvanceAmt = (it) => {
    const v = (it.variants || []).find(vv => vv.id === it.variant_id)
    const lineTotal = Number(v?.sell_price || it.unit_price || 0) * Number(it.quantity || 1)
    if (it.advance_payment_type === 'full') return lineTotal
    if (it.advance_payment_type === 'percent') return Math.ceil(lineTotal * Number(it.advance_payment_value || 0) / 100)
    return Number(it.advance_payment_value || 0)
  }
  const totalAdvance = advanceItems.reduce((sum, it) => sum + getAdvanceAmt(it), 0)

  const isInsideDhaka = address.district?.toLowerCase().includes('dhaka')
  const deliveryTime  = isInsideDhaka
    ? (taraSettings?.delivery_dhaka || '1-2 business days')
    : (taraSettings?.delivery_outside || '3-5 business days')

  // ── Validations ──────────────────────────────────────────────────────────────
  const validateDetails = () => {
    const e = {}
    if (!name.trim()) e.name = 'Please enter your full name'
    if (!address.district) e.district = 'Please select your district'
    if (!address.thana && !address.metro_thana) e.thana = 'Please select your area / thana'
    return e
  }
  const validateCart = () => {
    return cart.map(item => {
      if (!item.product_id) return 'Please select a product'
      if ((item.variants || []).length > 0 && !item.variant_id) return 'Please choose an option'
      return ''
    })
  }

  // ── Step navigation ───────────────────────────────────────────────────────────
  const goToDetails = () => {
    if (!lookupDone) { setPhoneError('Please check your phone number first'); return }
    setStep(2)
  }
  const goToCart = () => {
    const e = validateDetails()
    setDetailErrors(e)
    if (Object.keys(e).length > 0) return
    setAddressConfirmed(false); setStep(3)
  }
  const goToProfile = () => {
    const errs = validateCart()
    setCartErrors(errs)
    if (errs.some(e => e)) return
    setStep(4)
  }
  const goToReview = () => setStep(5)

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true); setSubmitError('')
    const fullPhone = countryCode + phone.replace(/^0+/, '')
    const spam = await checkSpam(seller.id, fullPhone)
    if (spam) { setSpamBlocked(true); setSubmitting(false); return }
    const addrParts = [address.house, address.road, address.moholla, address.neighbourhood || address.metro_thana || address.thana, address.union_name, address.district, address.division].filter(v => v && v !== 'N/A' && v !== '__na__' && v !== '__other__')
    const fullAddress = addrParts.join(', ')
    const rows = cart.filter(item => item.product_id).map(item => {
      const v = (item.variants || []).find(vv => vv.id === item.variant_id)
      return {
        seller_id: seller.id, customer_name: name.trim(), customer_phone: fullPhone,
        customer_address: fullAddress || null, customer_district: address.district || null,
        customer_note: note.trim() || null, product_code: item.product_code || null,
        product_name: item.product_name || null, variant_name: item.variant_name || null,
        quantity: Number(item.quantity || 1), unit_price: v?.sell_price || item.unit_price || null,
        source: 'order_form', status: 'pending',
      }
    })
    const { error } = await supabase.from('seller_order_form_submissions').insert(rows)
    if (error) { setSubmitError('Something went wrong. Please try again.'); setSubmitting(false); return }
    if (existingCustomer?.id && Object.values(profile).some(v => v)) {
      await supabase.from('customers').update({
        birthday: profile.birthday || null, anniversary: profile.anniversary || null,
        gender: profile.gender || null, age_range: profile.age_group || null,
        whatsapp: profile.whatsapp || null, email: profile.email || null,
        facebook_url: profile.facebook_url || null, instagram_url: profile.instagram_url || null,
      }).eq('id', existingCustomer.id)
    }
    setSubmitted(true); setSubmitting(false)
  }

  const fullPhone = countryCode + phone.replace(/^0+/, '')

  // ── Special screens ───────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: `3px solid ${BORDER}`, borderTopColor: TEAL, borderRadius: '50%', animation: 'qof-spin 0.7s linear infinite', margin: '0 auto 16px' }} />
        <div style={{ fontSize: '13px', color: MUTED, fontFamily: FONT_BODY }}>Loading...</div>
      </div>
    </div>
  )

  if (notFound) return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: FONT_BODY }}>
      <div style={{ textAlign: 'center', maxWidth: '340px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
        <div style={{ fontSize: '20px', fontWeight: 700, color: TEXT, fontFamily: FONT_UI, marginBottom: '8px' }}>Shop not found</div>
        <div style={{ fontSize: '13px', color: MUTED, lineHeight: 1.6 }}>This link may be incorrect. Please contact the seller directly on Facebook.</div>
        <div style={{ marginTop: '20px', fontSize: '11.5px', color: MUTED }}>Powered by <strong style={{ color: NAVY }}>PageConnect</strong></div>
      </div>
    </div>
  )

  if (formDisabled) return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: FONT_BODY }}>
      <div style={{ textAlign: 'center', maxWidth: '340px' }}>
        <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <TaraStarIcon size={28} color={TEAL} />
        </div>
        <div style={{ fontSize: '18px', fontWeight: 700, color: TEXT, fontFamily: FONT_UI, marginBottom: '8px' }}>{seller?.shop_name}</div>
        <div style={{ fontSize: '13px', color: MUTED, lineHeight: 1.6 }}>Online orders are temporarily paused. Please message the shop directly on Facebook.</div>
      </div>
    </div>
  )

  if (spamBlocked) return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: FONT_BODY }}>
      <div style={{ textAlign: 'center', maxWidth: '340px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <div style={{ fontSize: '20px', fontWeight: 700, color: TEXT, fontFamily: FONT_UI, marginBottom: '8px' }}>Already submitted</div>
        <div style={{ fontSize: '13px', color: MUTED, lineHeight: 1.6 }}>We received an order from this number recently. Please wait 10 minutes or message the shop on Facebook.</div>
      </div>
    </div>
  )

  if (submitted) return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: FONT_BODY }}>
      <div style={{ textAlign: 'center', maxWidth: '400px', width: '100%' }}>
        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg,#059669,#10B981)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 24px rgba(5,150,105,0.25)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke={WHITE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <div style={{ fontSize: '22px', fontWeight: 700, color: TEXT, fontFamily: FONT_UI, marginBottom: '8px' }}>Order Received! 🎉</div>
        <div style={{ fontSize: '13px', color: MUTED, lineHeight: 1.7, marginBottom: '20px' }}>
          Thank you, <strong style={{ color: TEXT }}>{name}</strong>! Your order has been sent to <strong style={{ color: TEXT }}>{seller?.shop_name}</strong>. They'll confirm and get back to you on Facebook Messenger.
        </div>
        <div style={{ padding: '16px', borderRadius: '12px', background: WHITE, border: `1px solid ${BORDER}`, marginBottom: '20px', textAlign: 'left' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', fontFamily: FONT_UI }}>Your order</div>
          {cart.filter(i => i.product_id).map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < cart.filter(x => x.product_id).length - 1 ? `1px solid ${BORDER}` : 'none' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT }}>{item.product_name}</div>
                {item.variant_name && <div style={{ fontSize: '11.5px', color: MUTED }}>{item.variant_name} × {item.quantity}</div>}
              </div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>
                {sym}{((item.variants?.find(v => v.id === item.variant_id)?.sell_price || item.unit_price || 0) * (item.quantity || 1)).toLocaleString()}
              </div>
            </div>
          ))}
          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: TEXT, fontFamily: FONT_UI }}>Total (excl. delivery)</span>
            <span style={{ fontSize: '15px', fontWeight: 700, color: TEAL, fontFamily: FONT_UI }}>{sym}{cartTotal.toLocaleString()}</span>
          </div>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 16px', borderRadius: '10px', background: NAVY }}>
          <TaraStarIcon size={13} color={TEAL} />
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', fontFamily: FONT_BODY }}>Powered by <strong style={{ color: WHITE }}>PageConnect</strong></span>
        </div>
      </div>
    </div>
  )

  // ── Main render ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;600&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        body{background:${BG};-webkit-font-smoothing:antialiased}
        @keyframes qof-spin{to{transform:rotate(360deg)}}
        @keyframes qof-up{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .qof-up{animation:qof-up 0.25s ease both}
        input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}
      `}</style>

      <div style={{ minHeight: '100vh', background: BG, fontFamily: FONT_BODY }}>

        {/* Shop Header */}
        <div style={{ background: NAVY, position: 'relative', overflow: 'hidden' }}>
          {seller?.cover_photo_url && (
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${seller.cover_photo_url})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.12 }} />
          )}
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '560px', margin: '0 auto', padding: '22px 20px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '10px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'rgba(255,255,255,0.1)', border: '1.5px solid rgba(45,212,176,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                {seller?.logo_url
                  ? <img src={seller.logo_url} alt={seller.shop_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <TaraStarIcon size={24} color={TEAL} />}
              </div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: WHITE, fontFamily: FONT_UI }}>{seller?.shop_name}</div>
                {seller?.tagline && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>{seller.tagline}</div>}
              </div>
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px 5px 8px', borderRadius: '20px', background: 'rgba(45,212,176,0.15)', border: '1px solid rgba(45,212,176,0.25)' }}>
              <TaraStarIcon size={12} color={TEAL} />
              <span style={{ fontSize: '11.5px', fontWeight: 600, color: TEAL, fontFamily: FONT_UI }}>Quick Order Form</span>
            </div>
            {seller?.order_form_note && (
              <div style={{ marginTop: '10px', padding: '10px 13px', borderRadius: '10px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '12.5px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                {seller.order_form_note}
              </div>
            )}
          </div>
        </div>

        {/* Step bar */}
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <StepBar current={step} total={5} labels={STEPS} />
        </div>

        {/* Step content */}
        <div style={{ maxWidth: '560px', margin: '0 auto', padding: '20px 16px 60px' }}>

          {/* STEP 1 — Phone */}
          {step === 1 && (
            <div className="qof-up">
              <SectionCard title="Your Phone Number" subtitle="We'll check if you've ordered here before" icon="📱">
                <PhoneInput
                  label="Phone number"
                  countryCode={countryCode} phone={phone}
                  onCountryCodeChange={setCountryCode}
                  onPhoneChange={v => { setPhone(v); setLookupDone(false); setExistingCustomer(null) }}
                  placeholder="01712345678" required/>
                {phoneError && <div style={{ fontSize: '11.5px', color: ERROR, marginTop: '-8px', marginBottom: '12px' }}>{phoneError}</div>}
                <Btn onClick={handleLookup} loading={lookingUp} fullWidth variant="outline">
                  {lookingUp ? 'Checking...' : 'Check my number'}
                </Btn>
                {lookupDone && (
                  <div style={{ marginTop: '14px', padding: '14px', borderRadius: '12px', background: existingCustomer ? 'rgba(5,150,105,0.06)' : BG, border: `1.5px solid ${existingCustomer ? 'rgba(5,150,105,0.2)' : BORDER}` }}>
                    {existingCustomer ? (
                      <>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: SUCCESS, fontFamily: FONT_UI, marginBottom: '4px' }}>
                          👋 Welcome back, {existingCustomer.name}!
                        </div>
                        <div style={{ fontSize: '12.5px', color: MUTED, lineHeight: 1.6, marginBottom: '12px' }}>
                          We found your details. Confirm or update your address in the next step.
                        </div>
                        <div style={{ padding: '10px 12px', borderRadius: '9px', background: WHITE, border: `1px solid ${BORDER}`, fontSize: '12.5px', color: TEXT, lineHeight: 1.8 }}>
                          <div><strong>{existingCustomer.name}</strong></div>
                          {existingCustomer.district && <div style={{ color: MUTED }}>{[existingCustomer.thana || existingCustomer.metro_thana, existingCustomer.district, existingCustomer.division].filter(Boolean).join(', ')}</div>}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, fontFamily: FONT_UI, marginBottom: '4px' }}>✨ New customer — welcome!</div>
                        <div style={{ fontSize: '12.5px', color: MUTED, lineHeight: 1.6 }}>Please fill in your name and delivery address in the next step.</div>
                      </>
                    )}
                  </div>
                )}
              </SectionCard>
              <Btn onClick={goToDetails} fullWidth size="lg" disabled={!lookupDone}>Continue →</Btn>
              {!lookupDone && <div style={{ textAlign: 'center', fontSize: '11.5px', color: MUTED, marginTop: '8px' }}>Check your phone number first</div>}
            </div>
          )}

          {/* STEP 2 — Details + Address */}
          {step === 2 && (
            <div className="qof-up">
              <SectionCard title="Your Details" subtitle="Confirm or update your information" icon="👤">
                <Field label="Full name" error={detailErrors.name}>
                  <Inp value={name} onChange={setName} placeholder="e.g. Riya Akter" error={detailErrors.name} />
                </Field>
                <div style={{ height: '1px', background: BORDER, margin: '4px 0 16px' }} />
                <div style={{ fontSize: '12px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px', fontFamily: FONT_UI }}>Delivery address</div>
                <BDAddressFields address={address} onChange={setA} isBD={true} />
                {(detailErrors.district || detailErrors.thana) && (
                  <div style={{ fontSize: '11.5px', color: ERROR, marginBottom: '12px' }}>{detailErrors.district || detailErrors.thana}</div>
                )}
                <Field label="House / Road / Area details" optional>
                  <Inp value={address.house} onChange={v => setA('house', v)} placeholder="House no, road, village, para, moholla..." />
                </Field>
              </SectionCard>

              {address.district && (
                <div className="qof-up" style={{ marginBottom: '16px' }}>
                  <CourierPreview address={address} name={name || '—'} phone={fullPhone} onEdit={() => setAddressConfirmed(false)} />
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '12px', cursor: 'pointer', padding: '12px 14px', borderRadius: '10px', background: addressConfirmed ? 'rgba(45,212,176,0.08)' : WHITE, border: `1.5px solid ${addressConfirmed ? TEAL : BORDER}` }}>
                    <input type="checkbox" checked={addressConfirmed} onChange={e => setAddressConfirmed(e.target.checked)}
                      style={{ width: '16px', height: '16px', marginTop: '1px', accentColor: TEAL, flexShrink: 0 }} />
                    <span style={{ fontSize: '12.5px', color: TEXT, lineHeight: 1.6 }}>
                      <strong>Yes, this address is correct</strong> — deliver my order here
                    </span>
                  </label>
                  {taraSettings && (
                    <div style={{ marginTop: '10px', padding: '10px 13px', borderRadius: '10px', background: BG, border: `1px solid ${BORDER}`, fontSize: '12px', color: MUTED, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🚚 Estimated delivery: <strong style={{ color: TEXT }}>{deliveryTime}</strong>
                      {taraSettings.delivery_express_note && <span>· {taraSettings.delivery_express_note}</span>}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <Btn onClick={() => setStep(1)} variant="ghost">← Back</Btn>
                <Btn onClick={goToCart} fullWidth size="lg" disabled={!addressConfirmed || !name.trim()}>
                  Continue to Order →
                </Btn>
              </div>
              {!addressConfirmed && address.district && <div style={{ textAlign: 'center', fontSize: '11.5px', color: MUTED, marginTop: '8px' }}>Please confirm your delivery address above</div>}
            </div>
          )}

          {/* STEP 3 — Cart */}
          {step === 3 && (
            <div className="qof-up">
              {allCodes.length > 0 && (
                <div style={{ padding: '10px 14px', borderRadius: '10px', background: 'rgba(45,212,176,0.08)', border: `1px solid rgba(45,212,176,0.2)`, marginBottom: '14px', fontSize: '12.5px', color: NAVY, display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <TaraStarIcon size={13} color={TEAL} />
                  Product{allCodes.length > 1 ? 's' : ''} pre-loaded from your chat. You can add more below.
                </div>
              )}

              {cart.map((item, i) => (
                <CartItem key={i} item={item} index={i} onUpdate={updateItem} onRemove={removeItem} sym={sym} canRemove={cart.length > 1} />
              ))}

              {cartErrors.some(e => e) && (
                <div style={{ padding: '10px 14px', borderRadius: '10px', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '12.5px', color: ERROR, marginBottom: '12px' }}>
                  Please complete all product selections above before continuing.
                </div>
              )}

              <button onClick={addItem}
                style={{ width: '100%', padding: '13px', borderRadius: '12px', border: `2px dashed ${TEAL}`, background: 'rgba(45,212,176,0.05)', fontSize: '13px', fontWeight: 700, color: TEAL, cursor: 'pointer', fontFamily: FONT_UI, marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                + Add another product
              </button>

              <SectionCard title="Note to seller" subtitle="Special instructions, gift message, etc." icon="📝">
                <textarea value={note} onChange={e => setNote(e.target.value)}
                  placeholder="e.g. Please gift wrap, or I need urgent delivery..."
                  rows={3}
                  style={{ ...baseInp, resize: 'none', lineHeight: 1.6 }}
                  onFocus={e => { e.target.style.borderColor = TEAL; e.target.style.boxShadow = '0 0 0 3px rgba(45,212,176,0.12)' }}
                  onBlur={e => { e.target.style.borderColor = BORDER; e.target.style.boxShadow = 'none' }} />
              </SectionCard>

              {cartTotal > 0 && (
                <div style={{ padding: '14px 18px', borderRadius: '12px', background: NAVY, marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '3px' }}>Total (excl. delivery)</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: WHITE, fontFamily: FONT_UI }}>{sym}{cartTotal.toLocaleString()}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '3px' }}>{cart.filter(i => i.product_id).length} item{cart.filter(i => i.product_id).length !== 1 ? 's' : ''}</div>
                    <div style={{ fontSize: '12px', color: TEAL, fontWeight: 600 }}>Delivery set by seller</div>
                  </div>
                </div>
              )}

              {/* Advance payment summary */}
              {advanceItems.length > 0 && (
                <div style={{ padding: '12px 14px', borderRadius: '10px', background: '#FFFBEB', border: '1px solid #FDE68A', marginBottom: '16px' }}>
                  <div style={{ fontSize: '11.5px', fontWeight: 700, color: '#92400E', marginBottom: '8px' }}>💳 Advance payment required for these items:</div>
                  {advanceItems.map((it, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', fontSize: '11.5px', color: '#92400E' }}>
                      <span>{it.product_name}{it.variant_name ? ' · ' + it.variant_name : ''}</span>
                      <strong>{sym}{getAdvanceAmt(it).toLocaleString()}{it.advance_payment_type === 'full' ? ' (full)' : ''}</strong>
                    </div>
                  ))}
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#92400E', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #FDE68A', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Total advance due now</span>
                    <span>{sym}{totalAdvance.toLocaleString()}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#92400E', marginTop: '4px' }}>Remaining balance payable on delivery</div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <Btn onClick={() => setStep(2)} variant="ghost">← Back</Btn>
                <Btn onClick={goToProfile} fullWidth size="lg">Continue →</Btn>
              </div>
            </div>
          )}

          {/* STEP 4 — Profile (optional) */}
          {step === 4 && (
            <div className="qof-up">
              <SectionCard title="A little about you" subtitle="" icon="🎁">
                <div style={{ padding: '12px 14px', borderRadius: '10px', background: 'rgba(45,212,176,0.07)', border: `1px solid rgba(45,212,176,0.2)`, marginBottom: '18px' }}>
                  <div style={{ fontSize: '12.5px', color: NAVY, lineHeight: 1.7 }}>
                    <strong>Why are we asking?</strong> {seller?.shop_name} uses this to send you personalised offers and birthday discounts. All fields are completely optional.
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                  <Field label="Birthday" optional marketing><Inp type="date" value={profile.birthday} onChange={v => setP('birthday', v)} /></Field>
                  <Field label="Anniversary" optional marketing><Inp type="date" value={profile.anniversary} onChange={v => setP('anniversary', v)} /></Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                  <Field label="Gender" optional marketing>
                    <Sel value={profile.gender} onChange={v => setP('gender', v)}>
                      <option value="">Prefer not to say</option>
                      <option value="Female">Female</option>
                      <option value="Male">Male</option>
                      <option value="Other">Other</option>
                    </Sel>
                  </Field>
                  <Field label="Age group" optional marketing>
                    <Sel value={profile.age_group} onChange={v => setP('age_group', v)}>
                      <option value="">Prefer not to say</option>
                      <option value="Under 18">Under 18</option>
                      <option value="18-24">18–24</option>
                      <option value="25-34">25–34</option>
                      <option value="35-44">35–44</option>
                      <option value="45-54">45–54</option>
                      <option value="55+">55+</option>
                    </Sel>
                  </Field>
                </div>
                <div style={{ height: '1px', background: BORDER, margin: '4px 0 16px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                  <Field label="WhatsApp" optional marketing><Inp value={profile.whatsapp} onChange={v => setP('whatsapp', v)} placeholder="017..." /></Field>
                  <Field label="Email" optional marketing><Inp type="email" value={profile.email} onChange={v => setP('email', v)} placeholder="you@email.com" /></Field>
                  <Field label="Facebook" optional marketing><Inp value={profile.facebook_url} onChange={v => setP('facebook_url', v)} placeholder="facebook.com/..." /></Field>
                  <Field label="Instagram" optional marketing><Inp value={profile.instagram_url} onChange={v => setP('instagram_url', v)} placeholder="instagram.com/..." /></Field>
                </div>
                <div style={{ fontSize: '11px', color: MUTED, lineHeight: 1.6, marginTop: '4px' }}>
                  🔒 Your information is only shared with {seller?.shop_name} and never sold to third parties.
                </div>
              </SectionCard>
              <div style={{ display: 'flex', gap: '10px' }}>
                <Btn onClick={() => setStep(3)} variant="ghost">← Back</Btn>
                <Btn onClick={goToReview} fullWidth size="lg">Review Order →</Btn>
              </div>
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <button onClick={goToReview} style={{ fontSize: '12px', color: MUTED, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  Skip — go straight to review
                </button>
              </div>
            </div>
          )}

          {/* STEP 5 — Review + Submit */}
          {step === 5 && (
            <div className="qof-up">
              {/* Order items */}
              <SectionCard title="Your Order" icon="🛒">
                {cart.filter(i => i.product_id).map((item, i) => {
                  const v = (item.variants || []).find(vv => vv.id === item.variant_id)
                  const lp = (v?.sell_price || item.unit_price || 0) * (item.quantity || 1)
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: i < cart.filter(x => x.product_id).length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                      {item.product_image
                        ? <img src={item.product_image} alt={item.product_name} style={{ width: '44px', height: '44px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
                        : <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: BG, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>📦</div>
                      }
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{item.product_name}</div>
                        <div style={{ fontSize: '11.5px', color: MUTED }}>
                          {item.variant_name && `${item.variant_name} · `}Qty: {item.quantity}
                        </div>
                        {item.advance_payment_type && item.advance_payment_type !== 'none' && (
                          <div style={{ fontSize: '10.5px', color: '#0D9488', marginTop: '2px' }}>
                            💳 {item.advance_payment_type === 'full' ? 'Full advance' : sym + getAdvanceAmt(item).toLocaleString() + ' advance required'}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT, flexShrink: 0 }}>{sym}{lp.toLocaleString()}</div>
                    </div>
                  )
                })}
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: TEXT, fontFamily: FONT_UI }}>Total (excl. delivery)</span>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: TEAL, fontFamily: FONT_UI }}>{sym}{cartTotal.toLocaleString()}</span>
                </div>
              </SectionCard>

              {/* Advance summary in review */}
              {advanceItems.length > 0 && (
                <div style={{ padding: '12px 14px', borderRadius: '10px', background: '#FFFBEB', border: '1px solid #FDE68A', marginBottom: '16px' }}>
                  <div style={{ fontSize: '11.5px', fontWeight: 700, color: '#92400E', marginBottom: '6px' }}>💳 Advance payment due now: {sym}{totalAdvance.toLocaleString()}</div>
                  <div style={{ fontSize: '11px', color: '#92400E' }}>Pay this advance to confirm your order. Remaining balance on delivery.</div>
                </div>
              )}

              {/* Delivery details */}
              <SectionCard title="Delivery To" icon="📦">
                <CourierPreview address={address} name={name} phone={fullPhone} onEdit={() => setStep(2)} />
                {taraSettings && (
                  <div style={{ marginTop: '10px', fontSize: '12.5px', color: MUTED, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    🚚 Estimated: <strong style={{ color: TEXT }}>{deliveryTime}</strong>
                    {taraSettings.delivery_express_note && ` · ${taraSettings.delivery_express_note}`}
                  </div>
                )}
              </SectionCard>

              {note && (
                <SectionCard title="Your Note" icon="📝">
                  <div style={{ fontSize: '13px', color: TEXT, lineHeight: 1.6 }}>{note}</div>
                </SectionCard>
              )}

              {submitError && (
                <div style={{ padding: '12px 14px', borderRadius: '10px', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '13px', color: ERROR, marginBottom: '16px' }}>
                  {submitError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                <Btn onClick={() => setStep(4)} variant="ghost">← Back</Btn>
                <Btn onClick={handleSubmit} loading={submitting} fullWidth size="lg" variant="teal">
                  <TaraStarIcon size={16} color={NAVY} /> Place Order
                </Btn>
              </div>

              <div style={{ textAlign: 'center', fontSize: '11.5px', color: MUTED, lineHeight: 1.7 }}>
                By placing this order you agree that your details will be shared with {seller?.shop_name}.
                <br />Powered by <strong style={{ color: NAVY }}>PageConnect</strong>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}